import { EventEmitter, Injectable, Output } from "@angular/core";
import { BehaviorSubject, Observable, ReplaySubject, from } from "rxjs";
import {
  CHECKOUT_ROUTE,
  LOCAL_STORAGE_KEYS,
  USER_ROUTES,
} from "../../constants/constants";
import { MERCHANT_ROUTES } from "../../global/constants";
import { HttpRequestsService } from "../http-requests.service";
import { ApiService } from "../api.service";
import { map, shareReplay, tap } from "rxjs/operators";
import { LocalStorageService } from "../local-storage.service";

@Injectable({
  providedIn: "root",
})
export class MerchantService {
  private historyData = new ReplaySubject<any>(1);
  @Output() aClickedEvent = new EventEmitter<any>();
  private loading$ = new BehaviorSubject<boolean>(false);
  public checkouts = new BehaviorSubject<any[]>([]);
  public isLoadingCheckout = new BehaviorSubject<boolean>(false);
  public isGetCheckouts = false;
  public profile: any;
  private profileRequest$: Observable<any> | null = null;
  public primaryMerchantUserId: number;
  public merchantId: number;
  public isSmartieFoods: boolean = false;
  constructor(
    private http: HttpRequestsService,
    private readonly apiService: ApiService,
    private readonly localstorage: LocalStorageService,
  ) {
    if (this.apiService.token) {
      this.getCheckouts();
    }
    this.setMerchantAndUserIds();
    this.isSmartFoodsMerchant();
  }

  async setMerchantAndUserIds() {
    this.merchantId = Number(this.localstorage.getLocalStore("merchantId"));
    this.primaryMerchantUserId = Number(
      this.localstorage.getLocalStore("primaryMerchantUserId"),
    );
    if (!this.primaryMerchantUserId) {
      const value = await this.localstorage.getDataFromIndexedDB(
        LOCAL_STORAGE_KEYS.ID,
      );
      if (Number(value)) {
        this.primaryMerchantUserId = Number(value);
      }
    }
  }

  async getPrimaryMerchantProfileForStaff(merchantId: number) {
    const result = await this.http.get(
      `${MERCHANT_ROUTES.PRIMARY_MERCHANT_PROFILE}/profile/${merchantId}`,
      false,
    );
    this.primaryMerchantUserId = result.data.merchant.userId;
    this.merchantId = merchantId;
    this.localstorage.setLocalStore("merchantId", this.merchantId);
    this.localstorage.setLocalStore(
      "primaryMerchantUserId",
      this.primaryMerchantUserId,
    );
    return result.data;
  }

  async isSmartFoodsMerchant() {
    const value =
      await this.localstorage.getDataFromIndexedDB("isSmartieFoods");
    this.isSmartieFoods = value === "true" || value === true;
    return this.isSmartieFoods;
  }

  async setPrimaryMerchantUserId(userId: number) {
    this.primaryMerchantUserId = userId;
    this.localstorage.setLocalStore("primaryMerchantUserId", userId);
  }

  getPrimaryMerchantUserId() {
    return this.primaryMerchantUserId;
  }

  getMerchantId() {
    return this.merchantId;
  }

  async merchantBalanceData() {
    return await this.http.get(USER_ROUTES.BALANCE_DATA, false);
  }

  async getMerchantTransactions(data) {
    return await this.http.post(USER_ROUTES.ANALYTICS_DATA, data, false);
  }

  getProfile(isReload: boolean = false): Observable<any> {
    if (isReload) {
      this.profile = null;
      this.profileRequest$ = null;
    }

    if (this.profile) {
      return from(Promise.resolve(this.profile));
    }

    if (this.profileRequest$) {
      return this.profileRequest$;
    }

    this.profileRequest$ = from(
      this.http.get(MERCHANT_ROUTES.PROFILE, false),
    ).pipe(
      map((res) => res.data),
      tap((response: any) => {
        this.profile = response;
        this.setProfileDataInLocalStorage(response);
      }),
      shareReplay(1),
    );

    return this.profileRequest$;
  }

  refreshProfile() {
    this.clearProfile();
    this.getProfile();
  }

  // Optional: Method to clear the cached profile
  clearProfile(): void {
    this.profile = null;
    this.profileRequest$ = null;
  }

  async isCurrentUserDriver() {
    const userDetailsStr = this.localstorage.getLocalStore("userDetails");
    const userDetails = userDetailsStr ? JSON.parse(userDetailsStr) : null;
    return userDetails?.userType === "DRIVER";
  }

  async setProfile() {
    const res: any = await this.http.get(MERCHANT_ROUTES.PROFILE, false);
    this.profile = res.data;
  }

  async merchantThingsToDo() {
    return await this.http.get(USER_ROUTES.MERCHANT_THINGS_TO_DO, false);
  }

  postClick = async (id) => {
    return await this.http.post(`${USER_ROUTES.POST_CLICK}/${id}`, false);
  };
  postBannerClose = async (id, merchantId) => {
    return await this.http.post(
      `${USER_ROUTES.POST_BANNER_CLOSE}/${id}/${merchantId}`,
      null,
      true,
    );
  };
  setHistoryData(data: any) {
    this.historyData.next(data);
  }

  getHistoryData() {
    return this.historyData.asObservable();
  }

  async getSettlementList(limit: number = 10, page: number = 1) {
    return await this.http.get(
      `${CHECKOUT_ROUTE.MERCHANT_SETTLEMENT_LIST}?page=${page}&limit=${limit}`,
    );
  }

  async getSettlementDetail(settlementId: number) {
    return await this.http.get(
      `${CHECKOUT_ROUTE.MERCHANT_SETTLEMENT_DETAIL}/${settlementId}`,
    );
  }

  async getDashboardGraphData() {
    return await this.http.get(`${USER_ROUTES.DASHBOARD_GRAPH_DATA}`, false);
  }
  async postTransactionDownload(data) {
    return await this.http.post(
      `${USER_ROUTES.TRANSACTION_STATEMENT}`,
      data,
      false,
    );
  }
  async UrlToBase64(data) {
    return await this.http.post(`${USER_ROUTES.URL_TO_BASE64}`, data, false);
  }

  async getCheckouts() {
    if (!this.isGetCheckouts) {
      this.isLoadingCheckout.next(true);
      const checkouts: any = await this.http.get(
        `${MERCHANT_ROUTES.CHECKOUTS}`,
        false,
      );
      this.checkouts.next(checkouts.data as any[]);
      this.isLoadingCheckout.next(false);
    }
    return this.checkouts.getValue();
  }

  setLoading(value: boolean) {
    this.loading$.next(value);
  }

  getLoading() {
    return this.loading$.asObservable();
  }

  sendBusinessDocumentsEmail() {
    return this.http.get(MERCHANT_ROUTES.SEND_DOCUMENT_REQUEST_EMAIL, false);
  }

  setProfileDataInLocalStorage(data: any) {
    this.localstorage.setLocalStore("businessname", data?.businessName);
    this.localstorage.setLocalStore("profile", data?.logo);
    this.localstorage.setLocalStore("userDetails", JSON.stringify(data));
    this.localstorage.setDataInIndexedDB(
      LOCAL_STORAGE_KEYS.MOBILECOUNTRYCODE,
      data?.phoneCountryCode,
    );
    this.localstorage.setDataInIndexedDB(
      LOCAL_STORAGE_KEYS.CURRENCY_SYMBOL,
      data?.currencySymbol,
    );
    this.localstorage.setDataInIndexedDB(
      LOCAL_STORAGE_KEYS.FIRSTNAME,
      data?.firstName,
    );
    this.localstorage.setDataInIndexedDB(
      LOCAL_STORAGE_KEYS.LASTNAME,
      data?.lastName,
    );
  }
}
